import { TAP_MAX_MOVE_PX } from "../constants";
import { REGION } from "./hitRegions";
import { createPressActions } from "./pressActions";
import { createPaneDrag } from "./paneDrag";
import { createPinchZoom } from "./pinchZoom";

const MOUSE = "mouse";
const MAIN_BUTTON = 0;
const PINCH_POINTERS = 2;

const distance = (first, second) => Math.hypot(first.x - second.x, first.y - second.y);


// The price axis is a zoom zone for time like the pane (prices always autoscale)
const PINCH_REGIONS = new Set([REGION.PANE, REGION.PRICE_AXIS]);

/**
 * Pointer events → gestures. The first pointer starts a drag or a tap;
 * a second finger in the pane turns the gesture into a pinch,
 * and the finger left after it pans on.
 *
 * @param {object} params
 * @param {HTMLElement} params.container
 * @param {object} params.view - see createInteraction
 * @param {object} params.hover - from createHoverFeedback
 * @param {(event: PointerEvent) => { x: number, y: number }} params.localPoint
 */
export const createPointerGestures = ({ container, view, hover, localPoint }) => {
  const { overlay, kinetic } = view;
  const actions = createPressActions(view);
  const pointers = new Map();
  // The pointer that started the gesture: { pointerId, start, region, isMoved }
  let press = null;
  let gesture = null;
  let isPinching = false;

  const startPinch = (event) => {
    gesture?.end({ time: event.timeStamp, isCancelled: true });
    press.isMoved = true;
    isPinching = true;
    gesture = createPinchZoom(view, ...pointers.values());
  };

  const continueAfterPinch = (event) => {
    const [[pointerId, point]] = pointers.entries();

    isPinching = false;
    press.pointerId = pointerId;
    gesture = createPaneDrag(view, {
      point,
      time: event.timeStamp,
      isTouch: true,
      canLongPress: false,
    });
  };

  return {
    get isPressed() {
      return pointers.size > 0;
    },

    /**
     * A finger must not scroll the page: two of them, or a gesture that moves
     * something. Only a drag in the pane may let it go (createPaneDrag).
     *
     * @returns {boolean}
     */
    get holdsPageScroll() {
      return pointers.size > 1 || Boolean(gesture && (gesture.holdsPageScroll?.() ?? true));
    },

    handlePointerDown: (event) => {
      const isMouse = event.pointerType === MOUSE;

      if (!overlay.layout || (isMouse && event.button !== MAIN_BUTTON)) return;

      const point = localPoint(event);

      pointers.set(event.pointerId, point);
      container.setPointerCapture(event.pointerId);
      kinetic.stop();

      if (pointers.size === PINCH_POINTERS && !isMouse && PINCH_REGIONS.has(press?.region)) {
        startPinch(event);
        return;
      }

      if (pointers.size > 1) return;

      press = {
        pointerId: event.pointerId,
        start: point,
        isMoved: false,
        ...actions.pressTarget(point, !isMouse),
      };
      gesture = actions.createDrag(press, event);

      if (isMouse) hover.update(point, press);
    },

    handlePointerMove: (event) => {
      const point = localPoint(event);

      if (pointers.has(event.pointerId)) {
        pointers.set(event.pointerId, point);

        if (isPinching) {
          gesture.move(...pointers.values());
        } else if (event.pointerId === press?.pointerId) {
          press.isMoved = press.isMoved || distance(point, press.start) >= TAP_MAX_MOVE_PX;
          gesture?.move(point, event.timeStamp);
        }
      }

      if (event.pointerType === MOUSE) hover.update(point, press);
    },

    /**
     * pointerup and pointercancel.
     *
     * @param {PointerEvent} event
     * @param {boolean} isCancelled - no tap and no inertia
     */
    finishPointer: (event, isCancelled) => {
      if (!pointers.delete(event.pointerId)) return;

      if (isPinching) {
        if (pointers.size === 1) continueAfterPinch(event);
        return;
      }

      if (event.pointerId !== press?.pointerId) return;

      const point = localPoint(event);

      gesture?.end({ time: event.timeStamp, isCancelled });
      gesture = null;

      if (!isCancelled && !press.isMoved) actions.handleTap(press, event, point);

      actions.release(press);
      press = null;

      if (event.pointerType === MOUSE) hover.update(point, null);
    },

    /** Stops the gesture without inertia, e.g. when the chart is destroyed. */
    cancel: () => {
      if (press) actions.release(press);
      gesture?.end({ time: performance.now(), isCancelled: true });
      gesture = null;
      press = null;
      isPinching = false;
      pointers.clear();
    },
  };
};
